/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Box from '@mui/material/Box';
import React from 'react';
import { WarningBlock } from './WarningBlock';
import {
  EDGE_PADDING,
  generateTicks,
  getTickLineStyle,
  getTimelineX,
  getTimeFromX,
} from './warningsTimelineUtils';
import type { TickType } from './warningsTimelineUtils';
import type { Warning } from '../../store/warningsForm/types';

export interface PositionedTick {
  time: Date;
  type: TickType;
  x: number;
}

export interface WarningLayout {
  key: string;
  left: number;
  top: number;
  warning: Warning;
  width: number;
}

interface TimelineBackgroundProps {
  contentWidth: number;
  scrollContainer: HTMLDivElement | null;
  timelineEnd: Date;
  timelineStart: Date;
}

interface TimelineTimeBarProps extends TimelineBackgroundProps {
  fullWidth: number;
}

interface WarningBlocksLayerProps {
  hoveredWarningId?: string;
  onHoverWarning?: (warningId?: string) => void;
  warningBlocks: WarningLayout[];
}

// A buffr of ticks outside the viewport
const TICK_OVERSCAN_PX = 240;

const getVisibleTicks = (
  scrollLeft: number,
  viewportWidth: number,
  timelineStart: Date,
  timelineEnd: Date,
  contentWidth: number,
): PositionedTick[] => {
  const tickWindowStartX = Math.max(0, scrollLeft - TICK_OVERSCAN_PX);
  const tickWindowEndX = Math.min(
    contentWidth + 2 * EDGE_PADDING,
    scrollLeft + viewportWidth + TICK_OVERSCAN_PX,
  );

  const tickWindowStart = getTimeFromX(
    tickWindowStartX,
    timelineStart,
    timelineEnd,
    contentWidth,
  );
  const tickWindowEnd = getTimeFromX(
    tickWindowEndX,
    timelineStart,
    timelineEnd,
    contentWidth,
  );

  return generateTicks(tickWindowStart, tickWindowEnd).map((tick) => ({
    ...tick,
    x: getTimelineX(tick.time, timelineStart, timelineEnd, contentWidth),
  }));
};

// Replacing the whole list is cheaper than taking care of many
// small tick elements on every horizontal scroll update.
const replaceChildren = (
  container: HTMLElement,
  nodes: HTMLElement[],
): void => {
  const fragment = document.createDocumentFragment();
  nodes.forEach((node) => fragment.appendChild(node));
  container.replaceChildren(fragment);
};

const useVisibleTicksRenderer = (
  layerRef: React.RefObject<HTMLDivElement | null>,
  scrollContainer: HTMLDivElement | null,
  renderTicks: (container: HTMLDivElement, ticks: PositionedTick[]) => void,
  contentWidth: number,
  timelineStart: Date,
  timelineEnd: Date,
): void => {
  React.useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer || !scrollContainer) {
      return undefined;
    }

    // Limit redraws to once per paint even if many small scroll events.
    let frame: number | null = null;

    const update = (): void => {
      frame = null;
      const ticks = getVisibleTicks(
        scrollContainer.scrollLeft,
        Math.max(scrollContainer.clientWidth, 1),
        timelineStart,
        timelineEnd,
        contentWidth,
      );
      renderTicks(layer, ticks);
    };

    const scheduleUpdate = (): void => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(update);
    };

    update();
    scrollContainer.addEventListener('scroll', scheduleUpdate, {
      passive: true,
    });

    return (): void => {
      scrollContainer.removeEventListener('scroll', scheduleUpdate);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [
    contentWidth,
    layerRef,
    renderTicks,
    scrollContainer,
    timelineEnd,
    timelineStart,
  ]);
};

// The backgrpund of the timeline with the tick lines
export const TimelineBackground: React.FC<TimelineBackgroundProps> = ({
  contentWidth,
  scrollContainer,
  timelineEnd,
  timelineStart,
}) => {
  const layerRef = React.useRef<HTMLDivElement>(null);

  const renderTicks = React.useCallback(
    (container: HTMLDivElement, ticks: PositionedTick[]) => {
      const nodes = ticks.map((tick) => {
        const lineStyle = getTickLineStyle(tick.type);
        const node = document.createElement('div');
        node.dataset.testid = 'timeline-tick-line';
        node.style.position = 'absolute';
        node.style.left = `${tick.x}px`;
        node.style.top = '0';
        node.style.bottom = '0';
        node.style.width = lineStyle.width;
        node.style.borderLeft = lineStyle.borderLeft;
        return node;
      });

      replaceChildren(container, nodes);
    },
    [],
  );

  useVisibleTicksRenderer(
    layerRef,
    scrollContainer,
    renderTicks,
    contentWidth,
    timelineStart,
    timelineEnd,
  );

  return <Box ref={layerRef} sx={{ position: 'absolute', inset: 0 }} />;
};

// A layer on top of the background with the block elements
export const WarningBlocksLayer = React.memo(
  ({
    warningBlocks,
    hoveredWarningId,
    onHoverWarning,
  }: WarningBlocksLayerProps) => {
    return (
      <>
        {warningBlocks.map((warningLayout) => (
          <WarningBlock
            highlighted={hoveredWarningId === warningLayout.warning.id}
            key={warningLayout.key}
            left={warningLayout.left}
            onHoverWarning={onHoverWarning}
            top={warningLayout.top}
            warning={warningLayout.warning}
            width={warningLayout.width}
          />
        ))}
      </>
    );
  },
);

const hasSameTicks = (
  currentTicks: PositionedTick[],
  nextTicks: PositionedTick[],
): boolean => {
  return (
    currentTicks.length === nextTicks.length &&
    currentTicks.every((tick, index) => {
      const nextTick = nextTicks[index];
      return (
        tick.x === nextTick.x &&
        tick.type === nextTick.type &&
        tick.time.getTime() === nextTick.time.getTime()
      );
    })
  );
};

// The time control at the bottom
export const TimelineTimeBar: React.FC<TimelineTimeBarProps> = ({
  contentWidth,
  fullWidth,
  scrollContainer,
  timelineEnd,
  timelineStart,
}) => {
  const [ticks, setTicks] = React.useState<PositionedTick[]>([]);

  React.useLayoutEffect(() => {
    if (!scrollContainer) {
      return undefined;
    }

    let frame: number | null = null;

    const update = (): void => {
      frame = null;
      setTicks((currentTicks) => {
        const nextTicks = getVisibleTicks(
          scrollContainer.scrollLeft,
          Math.max(scrollContainer.clientWidth, 1),
          timelineStart,
          timelineEnd,
          contentWidth,
        );

        return hasSameTicks(currentTicks, nextTicks) ? currentTicks : nextTicks;
      });
    };

    const scheduleUpdate = (): void => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(update);
    };

    update();
    scrollContainer.addEventListener('scroll', scheduleUpdate, {
      passive: true,
    });

    return (): void => {
      scrollContainer.removeEventListener('scroll', scheduleUpdate);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [contentWidth, scrollContainer, timelineEnd, timelineStart]);

  return (
    <Box
      sx={{
        width: fullWidth,
        position: 'relative',
        height: '100%',
      }}
    >
      {ticks
        .filter(
          (tick) => tick.type === 'day' || tick.time.getUTCHours() % 3 === 0,
        )
        .map((tick) => {
          const hour = tick.time.getUTCHours();
          const hourLabel = `${String(hour).padStart(2, '0')}:00`;
          const isDayBoundary = tick.type === 'day';
          const dateStr = tick.time.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            timeZone: 'UTC',
          });

          return (
            <Box
              data-testid="timeline-axis-label"
              key={`label-${tick.time.toISOString()}`}
              sx={{
                position: 'absolute',
                left: tick.x,
                top: 2,
                transform: 'translateX(-50%)',
                textAlign: 'center',
                lineHeight: 1.1,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: isDayBoundary ? 14 : 12,
                  fontWeight: isDayBoundary ? 'bold' : 'normal',
                  color: isDayBoundary ? '#333' : '#666',
                }}
              >
                {hourLabel}
              </Box>
              {isDayBoundary ? (
                <Box
                  sx={{
                    fontSize: 12,
                    color: '#666',
                  }}
                >
                  {dateStr}
                </Box>
              ) : null}
            </Box>
          );
        })}
    </Box>
  );
};
